/**
 * Ampel — Status-Indikator mit RICHTUNGSSEMANTIK (D-041).
 * Die FARBE kommt aus `guenstigPct` (= abweichung_guenstig_pct = abw × sign(soll)):
 * günstig>0 → grün-Richtung, ungünstig stark negativ → rot. Der ANGEZEIGTE Wert bleibt die
 * ROHE Abweichung (`abweichungPct`), damit der Nutzer sieht, was tatsächlich vom Plan abweicht.
 * HARTE Regel: guenstigPct null/undefined → grau (kein IST bzw. altes Schema ohne das Feld),
 * NIE die alte richtungsblinde Färbung. Barrierefrei: role="img" + aria-label.
 */
import { ampelStatus, AMPEL_LABELS, formatPct } from '../lib/format';
import type { AmpelStatus } from '../lib/format';

const DOT_CLASS: Record<AmpelStatus, string> = {
  green: 'bg-ampel-green',
  yellow: 'bg-ampel-yellow',
  red: 'bg-ampel-red',
  gray: 'bg-ampel-gray',
};

const SYMBOL: Record<AmpelStatus, string> = {
  green: '●',
  yellow: '●',
  red: '●',
  gray: '○',
};

export interface AmpelProps {
  /** Richtungs-korrigierte Abweichung — steuert die FARBE (D-041). null/undefined → grau. */
  guenstigPct?: number | null;
  /** Rohe Abweichung — der angezeigte %-Wert (was tatsächlich vom Plan abweicht). */
  abweichungPct?: number | null;
  /** Prozentwert neben dem Punkt anzeigen. */
  showValue?: boolean;
  className?: string;
}

export function Ampel({ guenstigPct, abweichungPct, showValue = false, className = '' }: AmpelProps) {
  const status = ampelStatus(guenstigPct);          // Farbe aus günstig (fehlt → grau)
  const label = AMPEL_LABELS[status];
  const valueText = status === 'gray' ? 'geplant' : formatPct(abweichungPct ?? guenstigPct);

  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`}>
      <span
        role="img"
        aria-label={`Ampel ${label}`}
        data-status={status}
        className={`inline-block h-3 w-3 rounded-full ${DOT_CLASS[status]}`}
      >
        <span className="sr-only">{SYMBOL[status]}</span>
      </span>
      {showValue && (
        <span
          className={status === 'gray' ? 'text-text-muted italic' : 'tabular-nums'}
          aria-hidden={false}
        >
          {valueText}
        </span>
      )}
    </span>
  );
}
